import { get, post, put, remove, API_URL } from '../utils/api';

/* Client-Side accountService.js
Location: client/src/services/accountService.js
Purpose: Acts as a client-side API wrapper that provides methods for the frontend to communicate with your backend APIs.
Key Functionality:
Creates axios-based methods to fetch account data from your endpoints
Handles all account-related API calls from the React frontend
Organizes API calls by domain (balance, usage, billing, etc.)
Provides error handling for each API request
Methods include:
getAccountBalance() - Fetches current balance information
getUsageData() - Retrieves electricity usage statistics
getBillingData() - Gets billing statements
getAccountDetails() - Fetches general account information
getAccountByUnit() - Gets account info based on site and unit
makePayment() - Processes payments
getPaymentHistory() - Retrieves payment history
getTransactionHistory() - Gets account transaction records
This service is consumed by your React contexts (like AccountDataContext) to provide data to your UI components. 
It abstracts away the API communication details so your React components can focus on rendering data rather than fetching it.
*/

/* HPD-1 Test VCS integration with YouTrack */

/**
 * This file provides account-related API services.
 * All requests are proxied through our auth-server which handles the communication with external APIs.
 */

// Track failed endpoint attempts
const failedEndpoints = {};

/**
 * Reset failed endpoints tracking
 * @param {string} endpoint - Optional specific endpoint to reset, if not provided all endpoints are reset
 */
export const resetFailedEndpoints = (endpoint = null) => {
  if (endpoint) {
    delete failedEndpoints[endpoint];
    // console.log(`Reset failure tracking for endpoint: ${endpoint}`);
  } else {
    // Reset all failed endpoints
    const count = Object.keys(failedEndpoints).length;
    Object.keys(failedEndpoints).forEach(key => {
      delete failedEndpoints[key];
    });
    // console.log(`Reset failure tracking for ${count} endpoints`);
  }
};

// Helper to track failed endpoints and determine if we should try again
const trackEndpointFailure = (endpoint) => {
  if (!failedEndpoints[endpoint]) {
    failedEndpoints[endpoint] = {
      count: 1,
      lastAttempt: Date.now()
    };
    return true; // First failure, try again
  }
  
  const lastAttempt = failedEndpoints[endpoint].lastAttempt;
  const now = Date.now();
  const timeDiff = (now - lastAttempt) / 1000; // seconds
  
  // Only retry after increasing delays (5s, 30s, 2min, 5min)
  let retryDelay;
  
  if (failedEndpoints[endpoint].count <= 1) {
    retryDelay = 5; // 5 seconds
  } else if (failedEndpoints[endpoint].count <= 2) {
    retryDelay = 30; // 30 seconds
  } else if (failedEndpoints[endpoint].count <= 3) {
    retryDelay = 120; // 2 minutes
  } else {
    retryDelay = 300; // 5 minutes
  }
  
  if (timeDiff > retryDelay) {
    // Update last attempt time
    failedEndpoints[endpoint].lastAttempt = now;
    failedEndpoints[endpoint].count += 1;
    return true; // Try again after delay
  }
  
  return false; // Don't try again yet
};

// Helper function to get site and unit info from localStorage
const getSiteAndUnitInfo = () => {
  const userDataString = localStorage.getItem('userData');
  let siteName = null;
  let unitId = null;
  
  if (userDataString) {
    try {
      const userData = JSON.parse(userDataString);
      siteName = userData.siteName;
      unitId = userData.unitId;
    } catch (e) {
      // console.error('Failed to parse userData from localStorage:', e);
    }
  }
  
  return { siteName, unitId };
};

/**
 * Get account balance information
 * @returns {Promise<object>} Account balance data
 */
export const getAccountBalance = async () => {
  // We need to use site and unit info instead of accountUuid
  const { siteName, unitId } = getSiteAndUnitInfo();
  
  // If we don't have site name or unit ID, we can't proceed
  if (!siteName || !unitId) {
    // console.error('Missing required parameters for getAccountBalance: siteName or unitId');
    return null;
  }
  
  const endpoint = `${API_URL}/account/by-unit`;
  // console.log(`Trying account balance endpoint: ${endpoint}`);
  
  // Check if we should try this endpoint 
  if (failedEndpoints[endpoint] && !trackEndpointFailure(endpoint)) {
    // console.log(`Skipping request to ${endpoint} due to recent failures`);
    return null;
  }
  
  try {
    const response = await get(endpoint, { site: siteName, unit: unitId });
    
    // If successful, reset failure count
    if (failedEndpoints[endpoint]) {
      delete failedEndpoints[endpoint];
    }
    
    // console.log('Account balance API response:', response.data);
    return response.data;
  } catch (error) {
    // console.error('Error fetching account balance:', error);
    
    // Track failure
    trackEndpointFailure(endpoint);
    
    throw error;
  }
};

/**
 * Get account by unit
 * @param {string} siteName - Site name
 * @param {string|number} unitId - Unit ID
 * @param {string} [siteUuid] - Site UUID (optional)
 * @param {string} [unitUuid] - Unit UUID (optional)
 * @returns {Promise<object>} Account information
 */
export const getAccountByUnit = async (siteName, unitId, siteUuid = null, unitUuid = null) => {
  // Make request to our auth-server API endpoint
  const endpoint = `${API_URL}/account/by-unit`;
  
  // console.log(`Trying account by unit endpoint: ${endpoint}`);
  
  // Check if we should try this endpoint 
  if (failedEndpoints[endpoint] && !trackEndpointFailure(endpoint)) {
    // console.log(`Skipping request to ${endpoint} due to recent failures`);
    return null;
  }
  
  try {
    // Build params object with all available identification info
    const params = { site: siteName, unit: unitId };
    
    // Add UUIDs if they're available to help backend optimize lookups
    if (siteUuid) params.siteUuid = siteUuid;
    if (unitUuid) params.unitUuid = unitUuid;
    
    const response = await get(endpoint, params);
    
    // If successful, reset failure count
    if (failedEndpoints[endpoint]) {
      delete failedEndpoints[endpoint];
    }
    
    // console.log('Account by unit API response:', response.data);
    return response.data;
  } catch (error) {
    // console.error('Error fetching account by unit:', error);
    
    // Track failure
    trackEndpointFailure(endpoint);
    
    throw error;
  }
};

/**
 * Get account details
 * @param {string} accountUuid - Account UUID
 * @returns {Promise<object>} Account details
 */
export const getAccountDetails = async (accountUuid) => {
  const endpoint = `${API_URL}/account/${accountUuid}`;
  
  // Check if we should try this endpoint
  if (failedEndpoints[endpoint] && !trackEndpointFailure(endpoint)) {
    // console.log(`Skipping request to ${endpoint} due to recent failures`);
    return null;
  }
  
  try {
    const response = await get(endpoint);
    
    // If successful, reset failure count
    if (failedEndpoints[endpoint]) {
      delete failedEndpoints[endpoint];
    }
    
    return response.data;
  } catch (error) {
    // console.error('Error fetching account details:', error);
    
    // Track failure
    trackEndpointFailure(endpoint);
    
    throw error;
  }
};

/**
 * Make a payment
 * @param {string} accountUuid - Account UUID
 * @param {object} paymentData - Payment data object
 * @returns {Promise<object>} Payment response
 */
export const makePayment = async (accountUuid, paymentData) => {
  const endpoint = `${API_URL}/account/${accountUuid}/payment`;
  
  // Check if we should try this endpoint
  if (failedEndpoints[endpoint] && !trackEndpointFailure(endpoint)) {
    // console.log(`Skipping request to ${endpoint} due to recent failures`);
    return null;
  }
  
  try {
    const response = await post(endpoint, paymentData);
    
    // If successful, reset failure count
    if (failedEndpoints[endpoint]) {
      delete failedEndpoints[endpoint];
    }
    
    return response.data;
  } catch (error) {
    // console.error('Error making payment:', error);
    
    // Track failure
    trackEndpointFailure(endpoint);
    
    throw error;
  }
};

/**
 * Get payment history
 * @param {string} accountUuid - Account UUID
 * @param {object} params - Query parameters for pagination and filtering
 * @returns {Promise<object>} Payment history
 */
export const getPaymentHistory = async (accountUuid, params = {}) => {
  const endpoint = `${API_URL}/account/${accountUuid}/payments`;
  
  // Check if we should try this endpoint
  if (failedEndpoints[endpoint] && !trackEndpointFailure(endpoint)) {
    // console.log(`Skipping request to ${endpoint} due to recent failures`);
    return null;
  }
  
  try {
    const response = await get(endpoint, params);
    
    // If successful, reset failure count
    if (failedEndpoints[endpoint]) {
      delete failedEndpoints[endpoint];
    }
    
    return response.data;
  } catch (error) {
    // console.error('Error fetching payment history:', error);
    
    // Track failure
    trackEndpointFailure(endpoint);
    
    throw error;
  }
};

/**
 * Get transaction history
 * @param {string} accountUuid - Account UUID
 * @param {object} params - Query parameters for pagination and filtering
 * @returns {Promise<object>} Transaction history
 */
export const getTransactionHistory = async (accountUuid, params = {}) => {
  const endpoint = `${API_URL}/account/${accountUuid}/transactions`;
  
  // Check if we should try this endpoint
  if (failedEndpoints[endpoint] && !trackEndpointFailure(endpoint)) {
    // console.log(`Skipping request to ${endpoint} due to recent failures`);
    return null;
  }
  
  try {
    const response = await get(endpoint, params);
    
    // If successful, reset failure count
    if (failedEndpoints[endpoint]) {
      delete failedEndpoints[endpoint];
    }
    
    return response.data;
  } catch (error) {
    // console.error('Error fetching transaction history:', error);
    
    // Track failure
    trackEndpointFailure(endpoint);
    
    throw error;
  }
}; 